import type { ReactElement } from "react";
import { ArticleHero } from "../ArticleHero";
import babyHeroImage from "../../assets/images/baby-outdoor-fall-portrait.webp";
import babyStudioImage7 from "../../assets/images/baby-girl-bunny-spring-studio-portrait.webp";
import babyStudioImage5 from "../../assets/images/baby-baker-themed-studio-portrait.webp";
import { ArticlePhoto } from "./ArticlePhoto";
import { UiIcon } from "./components/UiIcons";
import { GuideCtaActions } from "./components/GuideCtaActions";
import "./NewbornPages.scss";
import "./NewbornArticle.scss";
import "./BabyArticle.scss";

const checklist = [
  {
    icon: "clock" as const,
    title: "Protect the nap",
    text: "Choose a start time when your baby is usually rested and avoid squeezing the session between too many plans.",
  },
  {
    icon: "prepare" as const,
    title: "Pack familiar comforts",
    text: "Bring a favorite small toy, pacifier if used, wipes, diapers and a familiar snack or feeding supplies.",
  },
  {
    icon: "camera" as const,
    title: "Keep outfits simple",
    text: "Use comfortable pieces that fit now and bring one backup outfit in case of spills.",
  },
  {
    icon: "home" as const,
    title: "Leave extra travel time",
    text: "A calm arrival gives your baby a better chance to settle before photographs begin.",
  },
];

export const HowToPrepareForBabySession = (): ReactElement => (
  <main className="newborn-page newborn-article-page baby-article-page">
    <article>
      <ArticleHero
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Resources", to: "/resources/" },
          { label: "Baby", to: "/resources/baby/" },
          { label: "How to Prepare for Your Baby Photo Session" },
        ]}
        category="Baby preparation guide"
        description="A practical plan for naps, feeding, comfortable clothing and a photo day that stays relaxed."
        imageAlt="Baby ready for a comfortable milestone photography session"
        imagePosition="center 45%"
        imageSrc={babyHeroImage}
        readingTime="6 min read"
        title="How to Prepare for Your Baby Photo Session"
        updatedAt="2026-08-04"
        updatedLabel="Updated August 2026"
      />
      <div className="newborn-shell newborn-article-layout">
        <aside className="newborn-article-callout">
          <span className="newborn-article-callout__icon">
            <UiIcon name="star" />
          </span>
          <div>
            <h2>Quick answer</h2>
            <p>
              Plan around your baby’s most reliable rested window, offer a normal feed before leaving or when you
              arrive, and bring only a few familiar essentials. Comfortable clothing and an unhurried schedule matter
              more than trying to make the day perfect.
            </p>
          </div>
        </aside>
        <nav aria-label="In this guide" className="newborn-article-toc">
          <h2>In this guide</h2>
          <ol>
            <li>
              <a href="#checklist">Photo day checklist</a>
            </li>
            <li>
              <a href="#timing">Naps and timing</a>
            </li>
            <li>
              <a href="#feeding">Feeding and snacks</a>
            </li>
            <li>
              <a href="#clothes">What baby should wear</a>
            </li>
            <li>
              <a href="#bring">What to bring</a>
            </li>
            <li>
              <a href="#parents">Preparing parents</a>
            </li>
          </ol>
        </nav>
        <section className="newborn-article-section" id="checklist">
          <p className="newborn-article-kicker">THE FOUR THINGS THAT MATTER MOST</p>
          <h2>Photo Day Checklist</h2>
          <div className="baby-photo-checklist">
            {checklist.map((item) => (
              <article key={item.title}>
                <span>
                  <UiIcon name={item.icon} />
                </span>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
        <section className="newborn-article-section newborn-article-split" id="timing">
          <div>
            <p className="newborn-article-kicker">A RESTED WINDOW</p>
            <h2>Plan around naps, not a perfect schedule</h2>
            <p>
              Choose a time when your baby is normally awake and content. There is no need to keep them awake
              beforehand; overtired babies often find a new environment harder to enjoy.
            </p>
            <p>
              If the nap runs late or the morning changes unexpectedly, let me know. A small timing adjustment can be
              more helpful than rushing.
            </p>
          </div>
          <ArticlePhoto
            alt="Baby dressed as a baker during a playful studio milestone session"
            className="newborn-article-split__photo"
            label="PHOTO: RESTED BABY"
            src={babyStudioImage5}
          />
        </section>
        <section className="newborn-article-section" id="feeding">
          <p className="newborn-article-kicker">FAMILIAR ROUTINES</p>
          <h2>Feeding and snacks</h2>
          <p>
            Feed your baby normally rather than delaying food for the session. For older babies, bring a familiar, clean
            snack that does not stain hands or clothing. We can pause for feeding at any point.
          </p>
          <aside className="newborn-article-callout newborn-article-callout--tip">
            <span className="newborn-article-callout__icon">
              <UiIcon name="tip" />
            </span>
            <div>
              <h3>Avoid introducing something new</h3>
              <p>
                Photo day is not the best time for an unfamiliar snack, cup, outfit or toy. Familiar things help your
                baby feel secure.
              </p>
            </div>
          </aside>
        </section>
        <section className="newborn-article-section newborn-article-split newborn-article-split--reverse" id="clothes">
          <ArticlePhoto
            alt="Baby girl in a soft lace romper posing with a white bunny"
            className="newborn-article-split__photo"
            label="PHOTO: BABY OUTFIT"
            src={babyStudioImage7}
          />
          <div>
            <p className="newborn-article-kicker">COMFORTABLE AND TIMELESS</p>
            <h2>What should your baby wear?</h2>
            <p>
              Choose soft clothing that fits now, allows free movement and does not require constant adjusting. Simple
              rompers, knits, bloomers and bare feet photograph beautifully.
            </p>
            <p>
              Avoid oversized garments, large logos and stiff collars near the face. Bring one complete backup in case
              of a spill or diaper leak.
            </p>
          </div>
        </section>
        <section className="newborn-article-section" id="bring">
          <p className="newborn-article-kicker">PACK LIGHT, PACK SMART</p>
          <h2>What to bring</h2>
          <ul className="newborn-article-checklist">
            <li>
              <UiIcon name="check" />
              <span>Diapers, wipes and one complete spare outfit.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>Usual feeding supplies, water and an age-appropriate snack.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>One small favorite toy or comfort item.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>A brush, bib and cloth for quick cleanups.</span>
            </li>
          </ul>
        </section>
        <section className="newborn-article-section" id="parents">
          <p className="newborn-article-kicker">YOUR CALM HELPS</p>
          <h2>How parents can prepare</h2>
          <p>
            Wear something comfortable, keep expectations flexible and let me lead the photography. You do not need to
            stand behind me asking for smiles. Staying close, playing familiar games and responding normally to your
            baby creates a much calmer experience.
          </p>
        </section>
        <section className="newborn-article-cta baby-article-cta">
          <div>
            <p className="newborn-article-kicker">READY FOR PHOTO DAY</p>
            <h2>Let’s plan around your baby’s rhythm</h2>
            <p>Review the baby session options, or contact me with their age and routine if you need help planning photo day.</p>
          </div>
          <GuideCtaActions category="baby" />
        </section>
      </div>
    </article>
  </main>
);
export default HowToPrepareForBabySession;
